import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import BaseErrors from '@components/fields/BaseErrors';
import BooleanRadioButtonGroup from '@components/fields/BooleanRadioButtonGroup';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import DateInput from '@components/fields/DateInput';
import Input from '@components/fields/Input';
import RichTextArea from '@components/fields/RichTextArea';
import Form from '@components/forms/Form';
import styles from '@components/forms/InitialConsultConclusionForm.module.css';
import {
  CareProfileFragment as CareProfile,
  ClientRoleRoleEnum,
  ConsultFragment as Consult,
  GetJourneyWithInitialConsultQuery,
  InitialConsultReportFragment as InitialConsultReport,
  InitialConsultReportUpdateDocument,
  InitialConsultReportUpdateMutation,
  InitialConsultReportUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { clickCurrentTarget } from '@utils/keyboardUtils';
import { optionsForBoolean } from '@utils/optionUtils';

type FormData = {
  attendingClientIds: string[];
  attendingOther: string | null;
  benefitsExplained: boolean | null;
  clientFollowups: string | null;
  expertFollowups: string | null;
  needsMetBasicNeeds: boolean | null;
  needsMetCare: boolean | null;
  needsMetEmergencyPlanning: boolean | null;
  needsMetFinancialPlanning: boolean | null;
  needsMetFinancialPrograms: boolean | null;
  needsMetFood: boolean | null;
  needsMetInsurance: boolean | null;
  needsMetLegal: boolean | null;
  needsMetMedicalResources: boolean | null;
  needsMetMedications: boolean | null;
  needsMetMentalHealth: boolean | null;
  needsMetSocial: boolean | null;
  needsMetTransportation: boolean | null;
  needsMetYourWellbeing: boolean | null;
  nextConsult: string | null;
};

type Props = {
  careProfile: CareProfile;
  clientRoles: GetJourneyWithInitialConsultQuery['journey']['clientRoles'];
  consult: Consult | undefined;
  initialConsultReport: InitialConsultReport;
  journeyId: string;
  onSuccess: (nextPage: string) => void;
};

const InitialConsultConclusionForm = ({
  careProfile,
  clientRoles,
  consult,
  initialConsultReport,
  journeyId,
  onSuccess
}: Props) => {
  const [submitted, setSubmitted] = useState(false);
  const [backOrNext, setBackOrNext] = useState<boolean | null>(null);
  const [medicalNeeds, setMedicalNeeds] = useState<boolean | null>(null);
  const [functionalNeeds, setFunctionalNeeds] = useState<boolean | null>(null);
  const [financialNeeds, setFinancialNeeds] = useState<boolean | null>(null);

  let defaultNextConsultDate = null;
  if (careProfile.nextConsult) {
    defaultNextConsultDate = new Date(careProfile.nextConsult).toISOString().split('T')[0];
  }

  useEffect(() => {
    if (careProfile.needsMetMedications || careProfile.needsMetMentalHealth || careProfile.needsMetMedicalResources) {
      setMedicalNeeds(false);
    }
    if (
      careProfile.needsMetCare ||
      careProfile.needsMetFood ||
      careProfile.needsMetTransportation ||
      careProfile.needsMetEmergencyPlanning ||
      careProfile.needsMetBasicNeeds
    ) {
      setFunctionalNeeds(false);
    }
    if (
      careProfile.needsMetFinancialPrograms ||
      careProfile.needsMetInsurance ||
      careProfile.needsMetFinancialPlanning
    ) {
      setFinancialNeeds(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (backOrNext !== null && submitted) {
      if (backOrNext) {
        onSuccess('');
      } else {
        onSuccess('stage');
      }
      setSubmitted(false);
      setBackOrNext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backOrNext, submitted]);

  const attendeeIds = useMemo(() => {
    const ownerClientRole = clientRoles.find(role => role.role === ClientRoleRoleEnum.owner);
    const attendees =
      consult?.clientAttendees.clients
        .map(client => client.id)
        .filter(clientId => clientRoles.some(role => role.client.id === clientId)) ?? [];
    return attendees.length ? attendees : ownerClientRole ? [ownerClientRole.client.id] : [];
  }, [consult?.clientAttendees.clients, clientRoles]);

  const initialValues = {
    attendingClientIds: attendeeIds,
    attendingOther: initialConsultReport.consult?.clientAttendees.other ?? null,
    benefitsExplained: initialConsultReport.benefitsExplained ?? null,
    clientFollowups: initialConsultReport.clientFollowups ?? '',
    expertFollowups: initialConsultReport.expertFollowups ?? '',
    needsMetBasicNeeds: careProfile.needsMetBasicNeeds ?? null,
    needsMetCare: careProfile.needsMetCare ?? null,
    needsMetEmergencyPlanning: careProfile.needsMetEmergencyPlanning ?? null,
    needsMetFinancialPlanning: careProfile.needsMetFinancialPlanning ?? null,
    needsMetFinancialPrograms: careProfile.needsMetFinancialPrograms ?? null,
    needsMetFood: careProfile.needsMetFood ?? null,
    needsMetInsurance: careProfile.needsMetInsurance ?? null,
    needsMetLegal: careProfile.needsMetLegal ?? null,
    needsMetMedicalResources: careProfile.needsMetMedicalResources ?? null,
    needsMetMedications: careProfile.needsMetMedications ?? null,
    needsMetMentalHealth: careProfile.needsMetMentalHealth ?? null,
    needsMetSocial: careProfile.needsMetSocial ?? null,
    needsMetTransportation: careProfile.needsMetTransportation ?? null,
    needsMetYourWellbeing: careProfile.needsMetYourWellbeing ?? null,
    nextConsult: defaultNextConsultDate ?? null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateInitialConsultReport] = useMutation<
    InitialConsultReportUpdateMutation,
    InitialConsultReportUpdateMutationVariables
  >(InitialConsultReportUpdateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      attendingClientIds: yup
        .array()
        .required(intl.formatMessage({ id: 'errors.consultReport.clientAttendees.blank' })),
      attendingOther: yup.string().nullable().defined(),
      benefitsExplained: yup.boolean().defined().nullable(),
      clientFollowups: yup.string().nullable().defined(),
      expertFollowups: yup.string().nullable().defined(),
      needsMetBasicNeeds: yup.boolean().defined().nullable(),
      needsMetCare: yup.boolean().defined().nullable(),
      needsMetEmergencyPlanning: yup.boolean().defined().nullable(),
      needsMetFinancialPlanning: yup.boolean().defined().nullable(),
      needsMetFinancialPrograms: yup.boolean().defined().nullable(),
      needsMetFood: yup.boolean().defined().nullable(),
      needsMetInsurance: yup.boolean().defined().nullable(),
      needsMetLegal: yup.boolean().defined().nullable(),
      needsMetMedicalResources: yup.boolean().defined().nullable(),
      needsMetMedications: yup.boolean().defined().nullable(),
      needsMetMentalHealth: yup.boolean().defined().nullable(),
      needsMetSocial: yup.boolean().defined().nullable(),
      needsMetTransportation: yup.boolean().defined().nullable(),
      needsMetYourWellbeing: yup.boolean().defined().nullable(),
      nextConsult: yup.string().nullable().defined()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateInitialConsultReport({
      variables: {
        ...form.getValues(),
        clientAttendees: consult
          ? {
              clientIds: form.getValues().attendingClientIds,
              other: form.getValues().attendingOther
            }
          : undefined,
        consultId: consult?.id,
        id: initialConsultReport.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.initialConsultReportUpdate,
        intl,
        onSuccess: () => {
          setSubmitted(true);
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [consult, form, initialConsultReport.id, intl, updateInitialConsultReport]);

  const needOptions = useMemo(() => {
    const options = [true, false];

    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.initialConsultReport.questions.needs.${option ? 'met' : 'not_met'}`
      }),
      value: option
    }));
  }, [intl]);

  const booleanOptions = useMemo(() => optionsForBoolean(intl), [intl]);

  const attendeeOptions = useMemo(
    () =>
      clientRoles.map(option => ({
        label: option.client.fullName,
        value: option.client.id
      })),
    [clientRoles]
  );

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className={styles.formTitle}>
        <FormattedMessage id="models.initialConsultReport.questions.needs.content" />
      </div>
      <div className={styles.need}>
        <div className={styles.title}>
          <FormattedMessage id="models.initialConsultReport.questions.needs.medical" />
        </div>
        <div className={styles.needButtonGroup}>
          <div
            className={medicalNeeds === true ? styles.needOptionSelected : styles.needOption}
            onClick={() => setMedicalNeeds(true)}
            onKeyUp={clickCurrentTarget}
            tabIndex={0}
          >
            <FormattedMessage id="models.initialConsultReport.questions.needs.met" />
          </div>
          <div
            className={medicalNeeds === false ? styles.needOptionSelected : styles.needOption}
            onClick={() => setMedicalNeeds(false)}
            onKeyUp={clickCurrentTarget}
            tabIndex={0}
          >
            <FormattedMessage id="models.initialConsultReport.questions.needs.not_met" />
          </div>
        </div>
      </div>
      <div className={medicalNeeds === false ? 'ion-margin-start' : 'ion-hide'}>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.medications" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetMedications}
              name="needsMetMedications"
              options={needOptions}
              small
            />
          </div>
        </div>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.mentalHealth" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetMentalHealth}
              name="needsMetMentalHealth"
              options={needOptions}
              small
            />
          </div>
        </div>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.medicalResources" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetMedicalResources}
              name="needsMetMedicalResources"
              options={needOptions}
              small
            />
          </div>
        </div>
      </div>
      <div className={styles.need}>
        <div className={styles.title}>
          <FormattedMessage id="models.initialConsultReport.questions.needs.functional" />
        </div>
        <div className={styles.needButtonGroup}>
          <div
            className={functionalNeeds === true ? styles.needOptionSelected : styles.needOption}
            onClick={() => setFunctionalNeeds(true)}
            onKeyUp={clickCurrentTarget}
            tabIndex={0}
          >
            <FormattedMessage id="models.initialConsultReport.questions.needs.met" />
          </div>
          <div
            className={functionalNeeds === false ? styles.needOptionSelected : styles.needOption}
            onClick={() => setFunctionalNeeds(false)}
            onKeyUp={clickCurrentTarget}
            tabIndex={0}
          >
            <FormattedMessage id="models.initialConsultReport.questions.needs.not_met" />
          </div>
        </div>
      </div>
      <div className={functionalNeeds === false ? 'ion-margin-start' : 'ion-hide'}>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.care" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetCare}
              name="needsMetCare"
              options={needOptions}
              small
            />
          </div>
        </div>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.food" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetFood}
              name="needsMetFood"
              options={needOptions}
              small
            />
          </div>
        </div>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.transportation" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetTransportation}
              name="needsMetTransportation"
              options={needOptions}
              small
            />
          </div>
        </div>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.emergencyPlanning" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetEmergencyPlanning}
              name="needsMetEmergencyPlanning"
              options={needOptions}
              small
            />
          </div>
        </div>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.basicNeeds" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetBasicNeeds}
              name="needsMetBasicNeeds"
              options={needOptions}
              small
            />
          </div>
        </div>
      </div>
      <div className={styles.need}>
        <div className={styles.title}>
          <FormattedMessage id="models.initialConsultReport.questions.needs.legal" />
        </div>
        <div className={styles.buttonGroup}>
          <BooleanRadioButtonGroup
            allowDeselection
            control={form.control}
            error={form.formState.errors.needsMetLegal}
            name="needsMetLegal"
            options={needOptions}
            small
          />
        </div>
      </div>
      <div className={styles.need}>
        <div className={styles.title}>
          <FormattedMessage id="models.initialConsultReport.questions.needs.financial" />
        </div>
        <div className={styles.needButtonGroup}>
          <div
            className={financialNeeds === true ? styles.needOptionSelected : styles.needOption}
            onClick={() => setFinancialNeeds(true)}
            onKeyUp={clickCurrentTarget}
            tabIndex={0}
          >
            <FormattedMessage id="models.initialConsultReport.questions.needs.met" />
          </div>
          <div
            className={financialNeeds === false ? styles.needOptionSelected : styles.needOption}
            onClick={() => setFinancialNeeds(false)}
            onKeyUp={clickCurrentTarget}
            tabIndex={0}
          >
            <FormattedMessage id="models.initialConsultReport.questions.needs.not_met" />
          </div>
        </div>
      </div>
      <div className={financialNeeds === false ? 'ion-margin-start' : 'ion-hide'}>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.financialPrograms" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetFinancialPrograms}
              name="needsMetFinancialPrograms"
              options={needOptions}
              small
            />
          </div>
        </div>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.insurance" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetInsurance}
              name="needsMetInsurance"
              options={needOptions}
              small
            />
          </div>
        </div>
        <div className={styles.need}>
          <div className={styles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.needs.financialPlanning" />
          </div>
          <div className={styles.buttonGroup}>
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.needsMetFinancialPlanning}
              name="needsMetFinancialPlanning"
              options={needOptions}
              small
            />
          </div>
        </div>
      </div>
      <div className={styles.need}>
        <div className={styles.title}>
          <FormattedMessage id="models.initialConsultReport.questions.needs.social" />
        </div>
        <div className={styles.buttonGroup}>
          <BooleanRadioButtonGroup
            allowDeselection
            control={form.control}
            error={form.formState.errors.needsMetSocial}
            name="needsMetSocial"
            options={needOptions}
            small
          />
        </div>
      </div>

      <div className={styles.need}>
        <div className={styles.title}>
          <FormattedMessage id="models.initialConsultReport.questions.needs.yourWellbeing" />
        </div>
        <div className={styles.buttonGroup}>
          <BooleanRadioButtonGroup
            allowDeselection
            control={form.control}
            error={form.formState.errors.needsMetYourWellbeing}
            name="needsMetYourWellbeing"
            options={needOptions}
            small
          />
        </div>
      </div>
      <RichTextArea<FormData>
        control={form.control}
        error={form.formState.errors.expertFollowups}
        journeyId={journeyId}
        label={intl.formatMessage({ id: 'models.initialConsultReport.questions.conclusion.expertFollowups' })}
        name="expertFollowups"
      />
      <RichTextArea<FormData>
        control={form.control}
        error={form.formState.errors.clientFollowups}
        journeyId={journeyId}
        label={intl.formatMessage({ id: 'models.initialConsultReport.questions.conclusion.clientFollowups' })}
        name="clientFollowups"
      />
      <BooleanRadioButtonGroup
        allowDeselection
        control={form.control}
        error={form.formState.errors.benefitsExplained}
        inTracker={false}
        label={intl.formatMessage({ id: 'models.initialConsultReport.questions.conclusion.benefitsExplained' })}
        name="benefitsExplained"
        options={booleanOptions}
        small
      />
      <div className={consult ? '' : 'ion-hide'}>
        <CheckboxGroup<FormData>
          control={form.control}
          errors={form.formState.errors.attendingClientIds}
          label={intl.formatMessage({ id: 'forms.consultReport.clientAttendeesLabel' })}
          name="attendingClientIds"
          options={attendeeOptions}
          required
        />
        <Input<FormData>
          control={form.control}
          error={form.formState.errors.attendingOther}
          label={intl.formatMessage({ id: 'forms.consultReport.otherLabel' })}
          name="attendingOther"
        />
      </div>
      <DateInput
        control={form.control}
        error={form.formState.errors.nextConsult}
        futureOnly
        label={intl.formatMessage({ id: 'models.initialConsultReport.questions.conclusion.nextConsult' })}
        name="nextConsult"
        spellcheck
      />
      <div className={styles.spaceBetween}>
        <SecondaryButton
          data-test="submit-button"
          disabled={!form?.formState.isValid || form.formState.isSubmitting}
          onClick={() => setBackOrNext(false)}
          textKey="dictionary.previous"
          type="submit"
        />
        <PrimaryButton
          data-test="finish-report"
          disabled={!form?.formState.isValid || form.formState.isSubmitting}
          onClick={() => setBackOrNext(true)}
          textKey="dictionary.complete"
          type="submit"
        />
      </div>
    </Form>
  );
};

export default InitialConsultConclusionForm;
